import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, QrCode, Plus, Tray, WarningOctagon } from '@phosphor-icons/react';
import { PRIORITY_LABEL, type Priority } from '@helpdesk/shared';
import { getJson } from '../api/client.ts';
import { meQuery } from '../api/queries.ts';
import type { TicketCounts, TicketListItem, TicketListResult } from '../api/types.ts';
import { PriorityBadge, StatusBadge } from '../components/badges.tsx';
import { EmptyState, ErrorNote, ListSkeleton, PageTitle } from '../components/ui.tsx';
import { fmtAge } from '../lib/format.ts';

/** Startseite je Rolle (§10.1): Melder melden, Instandhalter arbeiten, Leitung überblickt. */
export function HomePage() {
  const me = useQuery(meQuery);
  const user = me.data?.user;
  if (!user) return null;
  return user.role === 'reporter' ? <ReporterHome name={user.displayName} /> : <WorkerHome lead={user.role === 'lead'} name={user.displayName} />;
}

/**
 * Ticketzeile für schmale Listen: Titel bekommt eine eigene Zeile, damit er
 * auch auf dem Handy lesbar bleibt; Nummer, Maschine und Alter darunter.
 */
function TicketRows({ result, empty }: { result: TicketListResult; empty: { title: string; hint?: string } }) {
  if (!result.items.length) return <EmptyState title={empty.title} hint={empty.hint} icon={<CheckCircle size={36} />} />;
  return (
    <ul className="divide-y divide-[var(--border-default)] border border-[var(--border-default)] bg-white">
      {result.items.map((t) => (
        <li key={t.id}>
          <TicketRow t={t} />
        </li>
      ))}
    </ul>
  );
}

function TicketRow({ t }: { t: TicketListItem }) {
  const open = !['resolved', 'closed', 'rejected'].includes(t.status);
  return (
    <Link
      to="/tickets/$number"
      params={{ number: t.display }}
      className={`block border-l-4 px-4 py-3 no-underline hover:bg-steel-50 ${t.priority === 'p1' && open ? 'border-l-arnold-red' : 'border-l-transparent'}`}
    >
      <div className="flex items-start gap-2.5">
        <PriorityBadge priority={t.priority} short />
        <span className="line-clamp-2 min-w-0 flex-1 text-[16px] leading-snug font-semibold text-steel-900">{t.title}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 pl-[42px] text-[14px] text-[var(--text-muted)]">
        <span className="font-mono text-steel-700">{t.display}</span>
        <StatusBadge status={t.status} />
        <span>{t.machineName ?? 'ohne Maschine'}</span>
        <span>{fmtAge(t.createdAt)}</span>
      </div>
    </Link>
  );
}

function SectionHead({ title, link }: { title: string; link?: { label: string; search: Record<string, string> } }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="text-[20px]">{title}</h2>
      {link ? (
        <Link to="/tickets" search={link.search as never} className="ad-link text-[15px] font-medium">
          {link.label}
        </Link>
      ) : null}
    </div>
  );
}

function ReporterHome({ name }: { name: string }) {
  const mine = useQuery({ queryKey: ['tickets', 'mine-home'], queryFn: () => getJson<TicketListResult>('/api/tickets', { tab: 'all', pageSize: 10 }) });
  return (
    <div className="space-y-7">
      <PageTitle lead={`Guten Tag, ${name.split(' ')[0]}. Eine Störung an einer Maschine? Hier melden oder den QR-Code an der Maschine scannen.`}>Instandhaltung</PageTitle>
      <div className="grid gap-3 sm:grid-cols-2">
        <Link to="/melden" className="ad-big-button">
          <Plus size={28} weight="bold" />
          Störung melden
        </Link>
        <Link to="/scan" className="ad-button-outline ad-button-lg w-full">
          <QrCode size={24} />
          QR-Code scannen
        </Link>
      </div>
      <section className="space-y-3">
        <SectionHead title="Meine Meldungen" link={{ label: 'Alle anzeigen', search: { tab: 'all' } }} />
        {mine.isPending ? (
          <ListSkeleton rows={3} />
        ) : mine.isError ? (
          <ErrorNote error={mine.error} />
        ) : (
          <TicketRows result={mine.data} empty={{ title: 'Noch keine Meldungen', hint: 'Was Sie melden, steht hier mit dem aktuellen Stand.' }} />
        )}
      </section>
    </div>
  );
}

type SiteOverview = {
  machinesDown: Array<{ id: string; name: string; inventory_no: string; site: string; since: string; ticket: number }>;
  oldestNew: Array<{ number: number; title: string; site: string; created_at: string; priority: Priority }>;
  openCounts: Array<{ site: string; priority: Priority; count: number }>;
};

type Tile = { label: string; value: number; search: Record<string, string>; alert?: boolean };

function WorkerHome({ lead, name }: { lead: boolean; name: string }) {
  const queue = useQuery({
    queryKey: ['tickets', 'home-queue'],
    queryFn: () => getJson<TicketListResult & { counts: TicketCounts }>('/api/tickets', { tab: 'unassigned', pageSize: 8 }),
    refetchInterval: 60_000,
  });
  const mine = useQuery({ queryKey: ['tickets', 'home-mine'], queryFn: () => getJson<TicketListResult>('/api/tickets', { tab: 'mine', pageSize: 8 }) });
  const overview = useQuery({ queryKey: ['reports', 'sites'], queryFn: () => getJson<SiteOverview>('/api/reports/sites'), enabled: lead, refetchInterval: 60_000 });
  const counts = queue.data?.counts;

  const tiles: Tile[] = counts
    ? [
        { label: 'Nicht zugewiesen', value: counts.unassigned, search: { tab: 'unassigned' }, alert: counts.unassigned > 0 },
        { label: 'Mir zugewiesen', value: counts.mine, search: { tab: 'mine' } },
        { label: 'P1 offen', value: counts.p1, search: { tab: 'open', priority: 'p1' }, alert: counts.p1 > 0 },
        { label: 'Offen gesamt', value: counts.open, search: { tab: 'open' } },
        { label: 'Wartend', value: counts.waiting, search: { tab: 'waiting' } },
        { label: 'Zu prüfen', value: counts.review, search: { tab: 'review' } },
      ]
    : [];

  return (
    <div className="space-y-7">
      <PageTitle lead={`Guten Tag, ${name.split(' ')[0]}.`}>{lead ? 'Standortübersicht' : 'Übersicht'}</PageTitle>

      {counts ? (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-6">
          {tiles.map((tile) => (
            <Link
              key={tile.label}
              to="/tickets"
              search={tile.search as never}
              className={`ad-card ad-card-clickable block border-l-4 p-4 no-underline ${tile.alert ? 'border-l-arnold-red' : 'border-l-transparent'}`}
            >
              <div className="text-[15px] leading-tight font-medium text-steel-700">{tile.label}</div>
              <div className={`mt-1.5 font-mono text-[32px] leading-none font-medium ${tile.alert ? 'text-arnold-red' : 'text-steel-900'}`}>{tile.value}</div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-6" aria-hidden>
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="ad-skeleton h-[86px]" />
          ))}
        </div>
      )}

      {lead && overview.data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="ad-card space-y-3">
            <h2 className="flex items-center gap-2 text-[20px]">
              <WarningOctagon size={24} weight="fill" className="text-arnold-red" />
              Steht gerade
            </h2>
            {overview.data.machinesDown.length ? (
              <ul className="divide-y divide-[var(--border-default)]">
                {overview.data.machinesDown.map((m) => (
                  <li key={m.id} className="flex flex-wrap items-baseline justify-between gap-x-3 py-2">
                    <Link to="/maschinen/$id" params={{ id: m.id }} className="ad-link font-semibold">
                      {m.name}
                    </Link>
                    <span className="text-[14px] text-[var(--text-muted)]">
                      {m.site}, {m.inventory_no}, seit {fmtAge(m.since)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="flex items-center gap-2 text-ok">
                <CheckCircle size={22} weight="fill" /> Keine Maschine steht.
              </p>
            )}
          </section>
          <section className="ad-card space-y-3">
            <h2 className="flex items-center gap-2 text-[20px]">
              <Tray size={24} className="text-steel-700" />
              Älteste unbearbeitete Meldungen
            </h2>
            {overview.data.oldestNew.length ? (
              <ul className="divide-y divide-[var(--border-default)]">
                {overview.data.oldestNew.map((t) => (
                  <li key={t.number} className="py-2">
                    <div className="flex items-start gap-2">
                      <PriorityBadge priority={t.priority} short />
                      <Link to="/tickets/$number" params={{ number: `IH-${t.number}` }} className="ad-link min-w-0 flex-1 font-semibold">
                        {t.title}
                      </Link>
                    </div>
                    <div className="mt-0.5 pl-[42px] text-[14px] text-[var(--text-muted)]">
                      IH-{t.number}, {t.site}, {fmtAge(t.created_at)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="flex items-center gap-2 text-ok">
                <CheckCircle size={22} weight="fill" /> Alles angenommen.
              </p>
            )}
            {overview.data.openCounts.length ? (
              <div className="flex flex-wrap gap-1.5 border-t border-[var(--border-default)] pt-3">
                {overview.data.openCounts.map((c) => (
                  <span key={`${c.site}-${c.priority}`} className="bg-steel-50 px-2 py-0.5 text-[14px] text-steel-800">
                    {c.site} {PRIORITY_LABEL[c.priority].split(' ')[0]}: <span className="font-mono font-medium">{c.count}</span>
                  </span>
                ))}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}

      <section className="space-y-3">
        <SectionHead title="Mir zugewiesen" link={{ label: 'Alle meine', search: { tab: 'mine' } }} />
        {mine.isPending ? (
          <ListSkeleton rows={2} />
        ) : mine.isError ? (
          <ErrorNote error={mine.error} />
        ) : (
          <TicketRows result={mine.data} empty={{ title: 'Nichts zugewiesen', hint: 'Unter „Nicht zugewiesen“ warten Tickets auf jemanden, der sie übernimmt.' }} />
        )}
      </section>
      <section className="space-y-3">
        <SectionHead title="Nicht zugewiesen" link={{ label: 'Alle anzeigen', search: { tab: 'unassigned' } }} />
        {queue.isPending ? (
          <ListSkeleton rows={3} />
        ) : queue.isError ? (
          <ErrorNote error={queue.error} />
        ) : (
          <TicketRows result={queue.data} empty={{ title: 'Alles verteilt', hint: 'Jedes offene Ticket hat einen Bearbeiter.' }} />
        )}
      </section>
    </div>
  );
}
